import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "@/lib/assets";

export function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(m.matches);
    const h = () => setReduced(m.matches);
    m.addEventListener("change", h);
    return () => m.removeEventListener("change", h);
  }, []);
  return reduced;
}

// Line-by-line masked headline reveal
export function MaskedReveal({ lines, className = "", delay = 0, testId }) {
  const reduced = useReducedMotion();
  return (
    <h1 className={className} data-testid={testId}>
      {lines.map((line, i) => (
        <span key={i} className="mask-line">
          <motion.span
            className="block"
            initial={reduced ? { y: 0 } : { y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: delay + i * 0.12 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

// Generic scroll reveal
export function Reveal({ children, className = "", y = 40, delay = 0, once = true }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduced ? { opacity: 1 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

// Word-by-word wind reveal
export function WindReveal({ text, className = "" }) {
  const reduced = useReducedMotion();
  const raw = text.split(" ");
  // keep the last two words together so a heading never ends in an orphan word
  const words = raw.length > 2 ? [...raw.slice(0, -2), raw.slice(-2).join("\u00a0")] : raw;
  return (
    <span className={className}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom">
          <motion.span
            className="inline-block"
            initial={reduced ? { y: 0 } : { y: "100%", opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: i * 0.03 }}
          >
            {w}&nbsp;
          </motion.span>
        </span>
      ))}
    </span>
  );
}

// Water-ripple button
export function RippleButton({ children, className = "", onClick, type = "button", testId, ...rest }) {
  const ref = useRef(null);
  const handle = (e) => {
    const host = ref.current;
    if (host) {
      const rect = host.getBoundingClientRect();
      const s = document.createElement("span");
      const size = Math.max(rect.width, rect.height);
      s.className = "ripple";
      s.style.width = s.style.height = `${size}px`;
      s.style.left = `${e.clientX - rect.left - size / 2}px`;
      s.style.top = `${e.clientY - rect.top - size / 2}px`;
      host.appendChild(s);
      setTimeout(() => s.remove(), 700);
    }
    onClick && onClick(e);
  };
  return (
    <button ref={ref} type={type} onClick={handle} data-testid={testId}
      className={`ripple-host ${className}`} {...rest}>
      {children}
    </button>
  );
}

// Watercolor birds flying across the hero on mount, then gently drifting
export function BirdReveal() {
  const reduced = useReducedMotion();
  if (reduced) {
    return (
      <div aria-hidden="true" className="wc-cutout pointer-events-none absolute top-[12%] right-[6%] w-40 z-20">
        <img src={ASSETS.birds} alt="" className="w-full" />
      </div>
    );
  }
  return (
    <div aria-hidden="true" className="wc-cutout pointer-events-none absolute top-[8%] left-0 w-40 lg:w-56 z-20">
      <motion.img
        src={ASSETS.birds} alt="" className="w-full"
        initial={{ x: "-30vw", y: 60, opacity: 0 }}
        animate={{ x: "66vw", y: [60, 0, -14, 0], opacity: 1 }}
        transition={{
          x: { duration: 3.2, ease: [0.22, 1, 0.36, 1], delay: 0.2 },
          opacity: { duration: 1.2, delay: 0.2 },
          y: { duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.2 },
        }}
      />
    </div>
  );
}

// Falling leaves canvas
export function FallingLeaves() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  useEffect(() => {
    if (reduced) return;
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const colors = ["#66371B", "#81754B", "#B4833D", "#3F3F2C"];
    const leaves = Array.from({ length: 18 }).map(() => ({
      x: Math.random() * canvas.width, y: Math.random() * -canvas.height,
      r: 4 + Math.random() * 6, s: 0.4 + Math.random() * 0.9,
      a: Math.random() * Math.PI * 2, sw: 0.6 + Math.random(),
      c: colors[Math.floor(Math.random() * colors.length)],
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      leaves.forEach((l) => {
        l.y += l.s; l.x += Math.sin(l.a) * l.sw; l.a += 0.01;
        if (l.y > canvas.height + 20) { l.y = -20; l.x = Math.random() * canvas.width; }
        ctx.save(); ctx.translate(l.x, l.y); ctx.rotate(l.a);
        ctx.globalAlpha = 0.55; ctx.fillStyle = l.c;
        ctx.beginPath(); ctx.ellipse(0, 0, l.r, l.r / 2.2, 0, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [reduced]);
  if (reduced) return null;
  return <canvas ref={ref} aria-hidden="true" className="pointer-events-none fixed inset-0 z-[2]" />;
}

// Watercolor branch/flower divider that reveals + drifts in on scroll.
// Blend lives on a static wrapper so multiply composites against the page.
export function BranchDivider({ flip = false, flowers = false }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "center center"] });
  const opacity = useTransform(scrollYProgress, [0, 0.7], [0, 0.95]);
  const x = useTransform(scrollYProgress, [0, 0.7], [flip ? 60 : -60, 0]);
  return (
    <div ref={ref} className="w-full flex justify-center py-10 overflow-hidden" aria-hidden="true">
      <div className="wc-cutout">
        <motion.img
          src={flowers ? ASSETS.flowers : ASSETS.branch} alt=""
          className="h-24 lg:h-32 w-auto object-contain"
          style={reduced ? { transform: flip ? "scaleX(-1)" : "none" } : { opacity, x, scaleX: flip ? -1 : 1 }}
        />
      </div>
    </div>
  );
}

// Fixed decorative branch accent for a page corner
export function BranchAccent({ position = "top-right" }) {
  const reduced = useReducedMotion();
  const pos = position === "top-right" ? "top-24 right-0" : "top-24 left-0";
  return (
    <div className={`wc-cutout pointer-events-none absolute ${pos} w-56 lg:w-80 z-0`} aria-hidden="true">
      <motion.img
        src={ASSETS.branch} alt=""
        className="w-full"
        style={{ scaleX: position === "top-right" ? -1 : 1 }}
        initial={reduced ? { opacity: 0.6 } : { opacity: 0, y: -20 }}
        animate={{ opacity: 0.6, y: 0 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
      />
    </div>
  );
}
